import React from 'react';
import { DecodedQRResult } from '@/types/qr';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { copyTextToClipboard } from '@/lib/qr/export';
import {
  ShieldCheck,
  AlertTriangle,
  ExternalLink,
  Copy,
  Wifi,
  Contact,
  CreditCard,
  Globe,
  MapPin,
  Calendar,
  Type
} from 'lucide-react';

interface SecurityInspectorModalProps {
  result: DecodedQRResult | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenInGenerator: (result: DecodedQRResult) => void;
}

export const SecurityInspectorModal: React.FC<SecurityInspectorModalProps> = ({
  result,
  isOpen,
  onClose,
  onOpenInGenerator
}) => {
  const { showToast } = useToast();

  if (!result) return null;

  const { rawText, detectedType, securityAnalysis } = result;

  const handleCopy = async () => {
    await copyTextToClipboard(rawText);
    showToast('success', 'Copied to Clipboard', 'Decoded content copied.');
  };

  const handleOpenUrl = () => {
    if (!securityAnalysis.isSafe) {
      if (!window.confirm('⚠️ Warning: This URL contains suspicious characteristics or connects directly to an installer file. Are you sure you want to navigate?')) {
        return;
      }
    }
    window.open(rawText, '_blank', 'noopener,noreferrer');
  };

  const typeIcons: Record<string, React.ReactNode> = {
    url: <Globe className="h-4 w-4 text-primary" />,
    wifi: <Wifi className="h-4 w-4 text-primary" />,
    vcard: <Contact className="h-4 w-4 text-primary" />,
    upi: <CreditCard className="h-4 w-4 text-primary" />,
    geo: <MapPin className="h-4 w-4 text-primary" />,
    event: <Calendar className="h-4 w-4 text-primary" />,
    text: <Type className="h-4 w-4 text-muted-foreground" />
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Decoded QR Result"
      description="Client-side parsed payload and security structure"
      maxWidth="lg"
    >
      <div className="space-y-4 pt-1">
        {/* Payload Type Badge */}
        <div className="flex items-center justify-between p-3.5 rounded-xl bg-muted border border-border">
          <div className="flex items-center gap-2">
            {typeIcons[detectedType] || <Type className="h-4 w-4" />}
            <span className="text-[14px] font-semibold text-foreground tracking-tight capitalize">
              {detectedType} Payload
            </span>
          </div>
          {securityAnalysis.isSafe ? (
            <Badge variant="success" size="sm">
              <ShieldCheck className="h-3 w-3" /> Safe Payload
            </Badge>
          ) : (
            <Badge variant="critical" size="sm">
              <AlertTriangle className="h-3 w-3" /> Security Alert
            </Badge>
          )}
        </div>

        {/* Security Warnings if any */}
        {securityAnalysis.warnings.length > 0 && (
          <div className="p-3.5 rounded-xl bg-warning/10 border border-warning/20 text-warning text-[12px] space-y-1">
            <p className="font-semibold flex items-center gap-1.5">
              <AlertTriangle className="h-4 w-4" />
              Security Advisories:
            </p>
            <ul className="list-disc list-inside space-y-0.5 opacity-90 pl-1">
              {securityAnalysis.warnings.map((w, idx) => (
                <li key={idx}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Decoded Content Showcase */}
        <div className="p-4 rounded-xl bg-muted border border-border space-y-2">
          <span className="text-[12px] font-normal text-muted-foreground">
            Raw Content
          </span>
          <p className="font-mono text-[14px] text-foreground break-all select-all leading-relaxed max-h-36 overflow-y-auto">
            {rawText}
          </p>
        </div>

        {/* URL Breakdown if URL */}
        {securityAnalysis.isUrl && (
          <div className="p-3.5 rounded-xl bg-muted border border-border text-[12px] space-y-2">
            <span className="font-normal text-muted-foreground block">
              URL Breakdown
            </span>
            <div className="grid grid-cols-2 gap-2 font-mono">
              <div className="p-2 rounded-lg bg-card border border-border">
                <span className="text-[10px] text-muted-foreground block">Host</span>
                <span className="text-foreground font-medium truncate block">
                  {securityAnalysis.hostname}
                </span>
              </div>
              <div className="p-2 rounded-lg bg-card border border-border">
                <span className="text-[10px] text-muted-foreground block">Protocol</span>
                <span className="text-foreground font-medium block">
                  {securityAnalysis.protocol}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-3 border-t border-border flex flex-wrap justify-end gap-2.5">
          <Button variant="outline" onClick={handleCopy} leftIcon={<Copy className="h-4 w-4" />}>
            Copy
          </Button>

          {securityAnalysis.isUrl && (
            <Button
              variant="secondary"
              onClick={handleOpenUrl}
              leftIcon={<ExternalLink className="h-4 w-4" />}
            >
              Open URL
            </Button>
          )}

          <Button
            variant="primary"
            onClick={() => {
              onOpenInGenerator(result);
              onClose();
            }}
          >
            Edit in QR Studio
          </Button>
        </div>
      </div>
    </Modal>
  );
};
